import { FieldError } from "../FieldParts";
import {
  Container,
  IconSlot,
  InputWrapper,
  Label,
  StyledInput,
} from "../shared.styles";
import { useSubmitFirstField } from "../useSubmitFirstField";

type TextInputMode = "text" | "email" | "url" | "search" | "tel";
type TextFieldType = "text" | "password";

type TextFieldBaseProps = {
  label?: React.ReactNode;
  name: string;
  value: string;
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: React.ReactNode;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  inputMode?: TextInputMode;
  maxLength?: number;
  placeholder?: string;
  type?: TextFieldType;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
};

const TextFieldBase = ({
  autoComplete,
  autoFocus,
  disabled,
  error,
  fullWidth,
  icon,
  inputMode = "text",
  label,
  maxLength,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  type = "text",
  value,
}: TextFieldBaseProps): React.ReactElement => (
  <Container $fullWidth={fullWidth}>
    {label && <Label htmlFor={name}>{label}</Label>}
    <InputWrapper $disabled={!!disabled} $error={!!error}>
      <StyledInput
        $error={!!error}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        data-testid={name}
        disabled={disabled}
        id={name}
        inputMode={inputMode}
        maxLength={maxLength}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        type={type}
        value={value}
      />
      {icon && <IconSlot $disabled={disabled}>{icon}</IconSlot>}
    </InputWrapper>
    <FieldError error={error} name={name} />
  </Container>
);

type TextFieldProps = {
  label: React.ReactNode;
  name: string;
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  inputMode?: TextInputMode;
  placeholder?: string;
  type?: TextFieldType;
  onValueChange?: (value: string) => void;
};

const TextField = ({
  name,
  onValueChange,
  ...props
}: TextFieldProps): React.ReactElement => {
  const { error, handleBlur, handleChange, value } =
    useSubmitFirstField<string>(name);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    handleChange(e);
    onValueChange?.(e.target.value);
  };

  return (
    <TextFieldBase
      {...props}
      error={error}
      name={name}
      onBlur={handleBlur}
      onChange={handleFieldChange}
      value={value}
    />
  );
};

export { TextField, TextFieldBase };
